import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import styled from 'styled-components';
import { get } from 'lodash';
import { Plus, RefreshCw, Trash2 } from 'lucide-react';
import CurrencyInput from '../../../../../components/CurrencyInput';
import InfiniteSelect from '../../../../../components/InfiniteSelect';
import ApiService from '../../../../../services/ApiService';

import { OrderSchemaType } from '../order.schema';
import { paymentMethodLabel } from '../constants';
import {
  Title,
  SubTitle,
  FormGrid,
  FormGroup,
  Label,
  Input,
  Select,
  Button,
  Separator,
  SwitchBox,
  SwitchInput,
  SwitchSlider,
} from '../style';

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const InstallmentsTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InstallmentRow = styled.div`
  display: grid;
  grid-template-columns: 48px 1fr 1fr 1.4fr 48px;
  gap: 16px;
  align-items: center;
`;

const HeaderRow = styled(InstallmentRow)`
  & > span {
    font-family: 'Inter', sans-serif;
    font-weight: 700;
    color: #303030;
  }
`;

const NumberCell = styled.span`
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  color: #696969;
  text-align: center;
`;

const ComissaoCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #F87171;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;

  &:hover {
    color: #DC2626;
  }
`;

const EmptyMessage = styled.p`
  font-family: 'Inter', sans-serif;
  color: #696969;
  font-size: 14px;
`;

const SummaryLine = styled.div<{ mismatch?: boolean }>`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: ${props => (props.mismatch ? '#F87171' : '#303030')};
  font-weight: 700;
`;

const toCurrency = (value: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const roundMoney = (value: number) => Math.round((value || 0) * 100) / 100;

/** Extrai os dias de vencimento do nome da condição ("30/60/90" → [30,60,90]; "A vista" → [0]). */
const parseConditionToOffsets = (name?: string): number[] => {
  if (!name) return [0];
  const normalized = name.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  if (normalized.includes('vista')) return [0];
  const offsets = name
    .split('/')
    .map(part => parseInt(part.replace(/\D/g, ''), 10))
    .filter(num => !isNaN(num));
  return offsets.length ? offsets : [0];
};

/** Soma `days` a uma data ISO (YYYY-MM-DD); retorna outra data ISO. */
const addDays = (isoDate: string | undefined, days: number): string => {
  const base = isoDate ? new Date(`${isoDate}T00:00:00`) : new Date();
  if (isNaN(base.getTime())) return '';
  base.setDate(base.getDate() + days);
  return base.toISOString().slice(0, 10);
};

/** Gera as parcelas a partir do total e dos offsets: valor = total/N (ajuste de centavos na última). */
const buildInstallments = (total: number, offsets: number[], orderDate?: string) => {
  const count = offsets.length || 1;
  const base = roundMoney(total / count);
  return offsets.map((offset, index) => {
    const isLast = index === count - 1;
    const valor = isLast ? roundMoney(total - base * (count - 1)) : base;
    return {
      numero: index + 1,
      valor,
      vencimento: addDays(orderDate, offset),
      comissaoAtiva: false,
      comissaoPercentual: undefined,
      diasOffset: offset,
    };
  });
};

const PaymentSection: React.FC = () => {
  const { control, register, watch, setValue, formState: { errors, isDirty } } = useFormContext<OrderSchemaType>();
  const { fields, append, remove, update, replace } = useFieldArray({ control, name: 'installments' });

  const total = Number(watch('total')) || 0;
  const orderDate = watch('orderDate');
  const paymentMethodId = watch('paymentMethodId');
  const paymentMethodName = watch('paymentMethodName');
  const paymentConditionId = watch('paymentConditionId');
  const paymentConditionName = watch('paymentConditionName');
  const installments = watch('installments') || [];

  // Catálogo global de formas de pagamento (PaymentMethod.name = codigo do gateway).
  const [paymentMethods, setPaymentMethods] = useState<{ id: number; name: string }[]>([]);
  useEffect(() => {
    ApiService.steelOrders
      .getPaymentMethods()
      .then((res) => setPaymentMethods(res.data ?? []))
      .catch(() => setPaymentMethods([]));
  }, []);

  const handleSelectMethod = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const id = event.target.value ? Number(event.target.value) : undefined;
      const name = paymentMethods.find((m) => m.id === id)?.name ?? '';
      setValue('paymentMethodId', id, { shouldDirty: true });
      setValue('paymentMethodName', name, { shouldDirty: true });
      if (name !== 'Outros') {
        setValue('paymentMethodDescription', undefined, { shouldDirty: true });
      }
    },
    [paymentMethods, setValue],
  );

  const selectedCondition = paymentConditionId
    ? { id: paymentConditionId, name: paymentConditionName ?? '' }
    : null;

  const fetchPaymentConditions = useCallback(async (limit: number, offset: number, search?: string) => {
    const page = Math.floor(offset / limit) + 1;
    const response = await ApiService.steelOrders.getPaymentConditions({ page, limit, search });
    return response.data?.data ?? [];
  }, []);

  const createPaymentConditionOption = useCallback(async (name: string) => {
    const response = await ApiService.steelOrders.createPaymentCondition({ name });
    return response.data;
  }, []);

  const handleSelectCondition = useCallback((option: any) => {
    setValue('paymentConditionId', option?.id, { shouldDirty: true });
    setValue('paymentConditionName', option?.name ?? '', { shouldDirty: true });
  }, [setValue]);

  // Re-split das parcelas: ao trocar a condição, ou no total/data se o form foi editado (isDirty evita clobber na hidratação).
  const lastConditionId = useRef<number | undefined>(paymentConditionId);
  const didInit = useRef(false);

  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true;
      lastConditionId.current = paymentConditionId;
      return;
    }
    if (!paymentConditionId) return;

    const conditionChanged = paymentConditionId !== lastConditionId.current;
    lastConditionId.current = paymentConditionId;

    if (!conditionChanged && !isDirty) return;

    const offsets = parseConditionToOffsets(paymentConditionName);
    replace(buildInstallments(total, offsets, orderDate));
  }, [paymentConditionId, paymentConditionName, total, orderDate, isDirty, replace]);

  const handleAddInstallment = useCallback(() => {
    append({
      numero: fields.length + 1,
      valor: 0,
      vencimento: orderDate ?? '',
      comissaoAtiva: false,
      comissaoPercentual: undefined,
    });
  }, [append, fields.length, orderDate]);

  // "Atualizar data": regenera apenas os vencimentos a partir da condição escolhida.
  const handleRefreshDueDates = useCallback(() => {
    const offsets = parseConditionToOffsets(paymentConditionName);
    fields.forEach((field, index) => {
      const offset = offsets[index] ?? offsets[offsets.length - 1] ?? 0;
      update(index, {
        ...installments[index],
        vencimento: addDays(orderDate, offset),
        diasOffset: offset,
      });
    });
  }, [fields, installments, paymentConditionName, orderDate, update]);

  const sumInstallments = installments.reduce(
    (acc: number, parcela: any) => acc + (Number(parcela?.valor) || 0),
    0,
  );
  const hasMismatch = fields.length > 0 && Math.abs(sumInstallments - total) > 0.01;

  return (
    <>
      <Title>Pagamento</Title>

      <FormGrid style={{ marginTop: '16px' }}>
        <FormGroup>
          <Label htmlFor="paymentConditionId">Condição de Pagamento</Label>
          <InfiniteSelect
            mode="crud"
            placeholder="Selecione ou crie (ex: 30/60/90)"
            searchPlaceholder="Buscar condição..."
            minCreateLength={2}
            allowDelete={false}
            showSelectedBelow={false}
            value={selectedCondition}
            getOptionLabel={(opt: any) => opt.name}
            hasError={!!get(errors, 'paymentConditionId')}
            fetchFunction={fetchPaymentConditions}
            onItemCreate={createPaymentConditionOption}
            onSelect={handleSelectCondition}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="paymentMethodId">Forma de Pagamento</Label>
          <Select
            id="paymentMethodId"
            value={paymentMethodId ?? ''}
            onChange={handleSelectMethod}
            hasError={!!get(errors, 'paymentMethodId')}
          >
            <option value="">Selecione</option>
            {paymentMethods.map(method => (
              <option key={method.id} value={method.id}>{paymentMethodLabel(method.name)}</option>
            ))}
          </Select>
        </FormGroup>

        {paymentMethodName === 'Outros' && (
          <FormGroup>
            <Label htmlFor="paymentMethodDescription">Descrição da Forma</Label>
            <Input
              id="paymentMethodDescription"
              {...register('paymentMethodDescription')}
              placeholder="Descreva a forma de pagamento"
              hasError={!!get(errors, 'paymentMethodDescription')}
            />
          </FormGroup>
        )}
      </FormGrid>

      <Separator />

      <SectionHeader>
        <SubTitle>Parcelas</SubTitle>
        <Button type="button" variant="outline" onClick={handleRefreshDueDates}>
          <RefreshCw size={16} />
          Atualizar data
        </Button>
      </SectionHeader>

      {fields.length === 0 && (
        <EmptyMessage>
          Selecione uma condição de pagamento ou adicione parcelas manualmente.
        </EmptyMessage>
      )}

      {fields.length > 0 && (
        <InstallmentsTable>
          <HeaderRow>
            <span style={{ textAlign: 'center' }}>Nº</span>
            <span>Valor</span>
            <span>Vencimento</span>
            <span>Comissão</span>
            <span />
          </HeaderRow>

          {fields.map((field, index) => {
            const comissaoAtiva = watch(`installments.${index}.comissaoAtiva`);
            return (
              <InstallmentRow key={field.id}>
                <NumberCell>{index + 1}</NumberCell>

                <Controller
                  name={`installments.${index}.valor`}
                  control={control}
                  render={({ field: valorField }) => (
                    <CurrencyInput
                      id={`installment-valor-${index}`}
                      name={valorField.name}
                      value={Number(valorField.value) || 0}
                      onValueChange={(value, name, values) => {
                        valorField.onChange(values?.float ?? 0);
                      }}
                      placeholder="R$ 0,00"
                    />
                  )}
                />

                <Input
                  type="date"
                  {...register(`installments.${index}.vencimento`)}
                />

                <ComissaoCell>
                  <Controller
                    name={`installments.${index}.comissaoAtiva`}
                    control={control}
                    render={({ field: comissaoField }) => (
                      <SwitchBox>
                        <SwitchInput
                          type="checkbox"
                          checked={comissaoField.value || false}
                          onChange={(e) => comissaoField.onChange(e.target.checked)}
                        />
                        <SwitchSlider className="slider" />
                      </SwitchBox>
                    )}
                  />
                  {comissaoAtiva && (
                    <Controller
                      name={`installments.${index}.comissaoPercentual`}
                      control={control}
                      render={({ field: percentualField }) => (
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="%"
                          style={{ maxWidth: 90 }}
                          value={percentualField.value ?? ''}
                          onChange={(e) =>
                            percentualField.onChange(
                              e.target.value === '' ? undefined : Number(e.target.value),
                            )
                          }
                        />
                      )}
                    />
                  )}
                </ComissaoCell>

                <RemoveButton type="button" onClick={() => remove(index)} title="Remover parcela">
                  <Trash2 size={18} />
                </RemoveButton>
              </InstallmentRow>
            );
          })}
        </InstallmentsTable>
      )}

      <Separator />

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Button type="button" variant="outline" onClick={handleAddInstallment}>
          <Plus size={16} />
          Adicionar parcela
        </Button>
      </div>

      {fields.length > 0 && (
        <SummaryLine mismatch={hasMismatch}>
          <span>Soma das parcelas: {toCurrency(sumInstallments)}</span>
          <span>·</span>
          <span>Total: {toCurrency(total)}</span>
        </SummaryLine>
      )}
    </>
  );
};

export default PaymentSection;
